import { useState, useEffect } from 'react';
import {
  Loader,
  Flex,
  Box,
  Label,
  IconButton,
  Button,
  Text,
  Dialog,
  DialogContentContainer,
  Menu,
  MenuDivider,
  MenuItem,
  SplitButton,
  SplitButtonMenu,
  Heading,
  Divider,
  Icon,
} from '@vibe/core';
import { shift } from '@floating-ui/react-dom';
import {
  Home,
  ExternalPage,
  TurnInto,
  Calendar,
  Email,
  Announcement,
  Video,
  Academy,
  DropdownChevronUp,
  DropdownChevronDown,
  Completed,
  Deactivate,
  Settings,
  Retry,
  Globe,
  Time,
  Clipboard,
  LearnMore,
  Person,
} from '@vibe/icons';
import { getRelativeTimeString } from '@gorilla/ui/src/components/RelativeTime';
import { ErrorBox as ErrorBoxCommon } from '@gorilla/ui/src/components/ErrorBox';
import { usePortal } from '../hooks/use-portal';
import { useWindowResize } from '../hooks/use-window-resize';
import { getClientPortalUrl } from '../lib/utils';
import { openPreviewWindow } from '../services/preview';
import { monday } from '../services/monday';
import { Register, RegisterAuthorize } from './Register';
import { Pages } from './Pages';
import { PortalSettings } from './PortalSettings';
import { ThemeSettings } from './ThemeSettings';
import { Dashboard } from './Dashboard';
import { PublishModal } from './PublishModal';
import { CustomizationModal } from './CustomizationModal';
import { PlaceholdersModal } from './PlaceholdersModal';
import { BoardSourceManageModal } from './BoardSourceManageModal';
import { EditBoardSourceModal } from './EditBoardSourceModal';
import ErrorBox from './ErrorBox';
import UploadPortalSettings from './UploadPortalSettings';
import { ConfirmModal } from './ConfirmModal';
import { ChangePreviewClientModal } from './ChangePreviewClientModal';
import { useItem } from '../hooks/use-items';
import { getStoredPreviewClientId, setStoredPreviewClientId } from '../lib/preview-client';
import { last } from 'lodash';
import { type BoardSource } from '@gorilla/portal-shared/src/schemas';

function PortalManager() {
  const [view, setView] = useState<'dashboard' | 'pages' | 'theme' | 'settings'>('dashboard');
  const [publishModal, setPublishModal] = useState(false);
  const [customizationModal, setCustomizationModal] = useState(false);
  const [showBoardSourceManageModal, setShowBoardSourceManageModal] = useState(false);
  const [editingBoardSource, setEditingBoardSource] = useState<BoardSource | null>(null);
  const [showPlaceholdersModal, setShowPlaceholdersModal] = useState(false);
  const [placeholderInsertHandler, setPlaceholderInsertHandler] = useState<((value: string) => void) | null>(null);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [confirmDiscardDraftModalOpen, setConfirmDiscardDraftModalOpen] = useState(false);
  const [confirmUnpublishPortalModalOpen, setConfirmUnpublishPortalModalOpen] = useState(false);
  const [changePreviewClientModalOpen, setChangePreviewClientModalOpen] = useState(false);
  const [previewClientIdOverride, setPreviewClientIdOverride] = useState<number | null>(null);

  const { width: windowWidth } = useWindowResize();

  let screenMode: 'small' | 'medium' | 'large' = 'large';

  if (windowWidth < 1250) {
    screenMode = 'medium';
  }

  if (windowWidth < 1000) {
    screenMode = 'small';
  }

  const {
    portal,
    portalSettings,
    setSettingsDraft,
    mondayContext,
    isPublishing,
    isUnpublishing,
    triggerSaveSettings,
    publish,
    unpublish,
    uiUpdateId,
    hasUnpublishedChanges,
    isPortalPublished,
    mondayAccount,
    me,
    boardSourceIdsInUse,
    clientBoard,
    clientBoardSubitemsBoardId,
    isSavingSettings,
    triggerUploadLogo,
    isUploadingLogo,

    portalState,

    clientBoardItems,
    clientBoardItemsError,
    isLoadingClientBoardItems,

    registryEntries,
    triggerVerifyCustomDomain,
    areRegistryEntriesLoading,
    registryEntriesError,
    isVerifyingCustomDomain,
    verifyCustomDomainError,
    triggerConnectCustomDomain,
    isConnectCustomDomainConnecting,
    customDomainError,
    triggerDisconnectCustomDomain,
    isConnectCustomDomainDisconnecting,
    disconnectCustomDomainError,
    region,

    triggerDiscardDraft,
    isDiscardingDraft,
    analyticsData,
  } = usePortal();

  // Preview client selection. Derived during render (synchronous localStorage read) so it
  // re-computes for free once `portal` loads; the override only holds an in-session change.
  const storedPreviewClientId = portal?.id ? getStoredPreviewClientId(portal.id) : null;
  const previewClientId = previewClientIdOverride ?? storedPreviewClientId;
  const { item: resolvedPreviewClient } = useItem(previewClientId ?? undefined);
  // The client to preview: the stored one if it still exists, otherwise the first client (fallback).
  const effectivePreviewClient = previewClientId != null && resolvedPreviewClient ? resolvedPreviewClient : clientBoardItems?.[0];

  useEffect(() => {
    // Scroll to top when view changes
    window.scrollTo(0, 0);
  }, [view]);

  if (portalState.state === 'init') {
    return (
      <Flex
        justify="center"
        align="center"
        style={{
          width: '100%',
          height: '100vh',
          textAlign: 'center',
        }}
      >
        <Loader color="primary" size={30} />
      </Flex>
    );
  }

  if (portalState.state === 'error') {
    if (['MISSING_INTEGRATION_USER'].includes(portalState.errorKind)) {
      return (
        <>
          <Register
            clientBoardItems={clientBoardItems}
            clientBoardItemsError={clientBoardItemsError}
            isLoadingClientBoardItems={isLoadingClientBoardItems}
            boardId={mondayContext?.boardId!}
            slug={mondayAccount?.slug}
          />
        </>
      );
    }
    if (['INVALID_INTEGRATION_USER', 'INTEGRATION_USER_NOT_BOARD_OWNER'].includes(portalState.errorKind)) {
      return (
        <>
          <RegisterAuthorize boardId={mondayContext?.boardId!} errorMessage={portalState.error} slug={mondayAccount?.slug} />
        </>
      );
    }

    if (portalState.errorKind === 'PORTAL_SETTINGS_INVALID') {
      return (
        <>
          <ErrorBox
            headline="Invalid portal settings"
            description="Download and email your portal settings as attachment to hello@getgorilla.app. We will validate and fix any present problems, and return the file back to you afterwards."
            buttonLabel="Download"
            onButtonClick={() => {
              const blob = new Blob([JSON.stringify(portal?.settings, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `portal-settings-${portal?.id}.json`;
              a.click();
            }}
            innerContent={
              <UploadPortalSettings onUploaded={(newPortalSettings) => setSettingsDraft({ ...portalSettings, ...newPortalSettings })} />
            }
          />
        </>
      );
    }

    if (portalState.errorKind === 'AUTHENTICATION') {
      return <ErrorBox headline="Authentication error" description={portalState.error} />;
    }

    if (portalState.errorKind === 'USER_NOT_OWNER') {
      return <ErrorBox headline="User not board owner" description={portalState.error} />;
    }

    return <ErrorBoxCommon title="There was a problem when trying to load the portal settings UI" error={portalState.rawError} />;
  }

  if (!portal) {
    return <ErrorBox headline="Error" description={'Portal not loaded'} />;
  }

  if (!analyticsData) {
    return <ErrorBox headline="Error" description={'Analytics data not loaded'} />;
  }

  if (!clientBoard) {
    return <ErrorBox headline="Error" description={'Clients board not loaded'} />;
  }

  if (!mondayAccount) {
    return <ErrorBox headline="Error" description={'Monday account not loaded'} />;
  }

  if (!me) {
    return <ErrorBox headline="Error" description={'Monday user not loaded'} />;
  }

  let content = null;

  if (view === 'pages') {
    content = (
      <Box
        className={`portalmanager__content--wrapper portalmanager__content--sidebaroffsetleft`}
        style={
          {
            //paddingLeft: screenMode === 'small' ? '240px' : '320px',
            //paddingLeft: screenMode === 'small' ? '0px' : '320px',
          }
        }
      >
        <Pages
          key={`portal-pages-${uiUpdateId}`}
          clientBoard={clientBoard}
          clientBoardItems={clientBoardItems}
          boardSourceIdsInUse={boardSourceIdsInUse}
          portal={portal}
          portalSettings={portalSettings}
          onChange={setSettingsDraft}
          customizationModal={customizationModal}
          hasUnpublishedChanges={hasUnpublishedChanges}
          setCustomizationModal={setCustomizationModal}
          clientBoardSubitemsBoardId={clientBoardSubitemsBoardId}
          onManageBoardSourcesClick={() => {
            setShowBoardSourceManageModal(true);
          }}
          onEditBoardSourceClick={(boardSourceId) => {
            const boardSource = portalSettings.boardSources.find((b) => b.id === boardSourceId);
            if (boardSource) {
              setEditingBoardSource(boardSource);
            }
          }}
          onShowPlaceholdersModalClick={(insertHandler?: (value: string) => void) => {
            setPlaceholderInsertHandler(() => insertHandler ?? null);
            setShowPlaceholdersModal(true);
          }}
          screenMode={screenMode}
          onPreviewPortal={() => {
            if (clientBoardItems && clientBoardItems.length > 0) {
              if (!portal.lastUpdatedAt) {
                triggerSaveSettings(portalSettings).then(() => {
                  openPreviewWindow(clientBoard!.id, effectivePreviewClient!, 'draft');
                });
              } else {
                openPreviewWindow(clientBoard!.id, effectivePreviewClient!, 'draft');
              }
            }
          }}
        />
      </Box>
    );
  } else if (view === 'dashboard') {
    content = (
      <Dashboard
        key={`portal-dashboard-${uiUpdateId}`}
        themeSettings={portalSettings.theme}
        analyticsData={analyticsData!}
        registryEntries={registryEntries}
        portalSettings={portalSettings}
        isPortalPublished={isPortalPublished}
        hasUnpublishedChanges={hasUnpublishedChanges}
        region={region}
        portal={portal}
        portalManagerPermissions={portal.portalManagerPermissions || []}
        mondayAccountSlug={mondayAccount.slug}
        screenMode={screenMode}
        setView={setView}
      />
    );
  } else if (view === 'theme') {
    content = (
      <ThemeSettings
        key={`portal-theme-${uiUpdateId}`}
        themeSettings={portalSettings.theme}
        onChange={(newTheme) => {
          setSettingsDraft({ ...portalSettings, theme: newTheme });
        }}
        clientBoard={clientBoard!}
        isUploadingLogo={isUploadingLogo}
        onLogoUpload={(uploadSettings) => {
          return triggerUploadLogo(uploadSettings);
        }}
        screenMode={screenMode}
        hasUnpublishedChanges={hasUnpublishedChanges}
      />
    );
  } else if (view === 'settings') {
    content = (
      <PortalSettings
        key={`portal-settings-${uiUpdateId}`}
        portalSettings={portalSettings}
        onChange={setSettingsDraft}
        customizationModal={customizationModal}
        setCustomizationModal={setCustomizationModal}
        registryEntries={registryEntries}
        triggerVerifyCustomDomain={triggerVerifyCustomDomain}
        areRegistryEntriesLoading={areRegistryEntriesLoading}
        registryEntriesError={registryEntriesError}
        isVerifyingCustomDomain={isVerifyingCustomDomain}
        verifyCustomDomainError={verifyCustomDomainError}
        triggerConnectCustomDomain={triggerConnectCustomDomain}
        isConnectCustomDomainConnecting={isConnectCustomDomainConnecting}
        customDomainError={customDomainError}
        triggerDisconnectCustomDomain={triggerDisconnectCustomDomain}
        isConnectCustomDomainDisconnecting={isConnectCustomDomainDisconnecting}
        disconnectCustomDomainError={disconnectCustomDomainError}
        portal={portal!}
        onUnpublishClick={async () => {
          await unpublish();
        }}
        isUnpublishing={isUnpublishing}
        clientBoardId={clientBoard.id}
        mondayAccountSlug={mondayAccount.slug}
        hasUnpublishedChanges={!!hasUnpublishedChanges}
        onDiscardDraftClick={async () => {
          await triggerDiscardDraft();
        }}
        isDiscardingDraft={isDiscardingDraft}
        portalManagerPermissions={portal?.portalManagerPermissions || []}
        screenMode={screenMode}
        onShowPlaceholdersModalClick={() => {}}
      />
    );
  }

  return (
    <div>
      <CustomizationModal
        isOpen={customizationModal}
        onClose={() => {
          setCustomizationModal(false);
        }}
      />
      {changePreviewClientModalOpen && clientBoard ? (
        <ChangePreviewClientModal
          isOpen={changePreviewClientModalOpen}
          boardId={clientBoard.id}
          value={previewClientId}
          onClose={() => setChangePreviewClientModalOpen(false)}
          onSave={(id) => {
            if (portal?.id) setStoredPreviewClientId(portal.id, id);
            setPreviewClientIdOverride(id);
          }}
        />
      ) : null}
      {publishModal ? (
        <PublishModal
          registryEntries={registryEntries}
          isPublishing={isPublishing}
          isOpen={publishModal}
          mode={isPortalPublished ? 'update' : 'publish'}
          isSaving={isSavingSettings}
          setShowPreviewWindow={() => {
            if (clientBoardItems && clientBoardItems.length > 0) {
              if (!portal.lastUpdatedAt) {
                triggerSaveSettings(portalSettings).then(() => {
                  openPreviewWindow(clientBoard!.id, effectivePreviewClient!, 'draft');
                });
              } else {
                openPreviewWindow(clientBoard!.id, effectivePreviewClient!, 'draft');
              }
            }
          }}
          onClose={() => {
            setPublishModal(false);
          }}
          publish={async () => {
            try {
              await publish();
            } catch (err) {
              setPublishModal(false);
            }
          }}
          portal={portal}
        />
      ) : null}
      {showBoardSourceManageModal ? (
        <BoardSourceManageModal
          portalSettings={portalSettings}
          onEditClick={(boardSource) => {
            setShowBoardSourceManageModal(false);
            setEditingBoardSource(boardSource);
          }}
          onDeleteClick={(boardSource) => {
            setSettingsDraft({
              ...portalSettings,
              boardSources: portalSettings.boardSources.filter((b) => b.id !== boardSource.id),
            });
          }}
          onClose={() => {
            setShowBoardSourceManageModal(false);
          }}
        />
      ) : null}
      {editingBoardSource ? (
        <EditBoardSourceModal
          boardSource={editingBoardSource}
          onClose={(updatedBoardSource) => {
            if (updatedBoardSource) {
              setSettingsDraft({
                ...portalSettings,
                boardSources: portalSettings.boardSources.map((b) => (b.id === updatedBoardSource.id ? updatedBoardSource : b)),
              });
            }
            setEditingBoardSource(null);
          }}
        />
      ) : null}
      {showPlaceholdersModal && clientBoard ? (
        <PlaceholdersModal
          onClose={() => {
            setShowPlaceholdersModal(false);
            setPlaceholderInsertHandler(null);
          }}
          clientBoard={clientBoard}
          portalSettings={portalSettings}
          isInsertMode={!!placeholderInsertHandler}
          onInsertPlaceholder={(value: string) => {
            if (placeholderInsertHandler) {
              placeholderInsertHandler(value);
            }
            setShowPlaceholdersModal(false);
            setPlaceholderInsertHandler(null);
          }}
        />
      ) : null}
      {confirmDiscardDraftModalOpen ? (
        <ConfirmModal
          isOpen={confirmDiscardDraftModalOpen}
          isConfirming={isDiscardingDraft}
          headline="Discard changes"
          text="Are you sure you want to discard your changes?"
          info="All unpublished changes will be deleted. Your current working draft will revert to the last published version."
          confirmButtonLabel="Discard changes"
          onConfirm={async () => {
            await triggerDiscardDraft();
            setConfirmDiscardDraftModalOpen(false);
          }}
          onClose={() => {
            setConfirmDiscardDraftModalOpen(false);
          }}
        />
      ) : null}
      {confirmUnpublishPortalModalOpen ? (
        <ConfirmModal
          isOpen={confirmUnpublishPortalModalOpen}
          isConfirming={isUnpublishing}
          headline="Unpublish portal"
          text="Are you sure you want to unpublish your portal?"
          info="By unpublishing the portal, clients will no longer be able to access the portal. Publish again to restore access."
          confirmButtonLabel="Unpublish portal"
          onConfirm={async () => {
            unpublish();
            setConfirmUnpublishPortalModalOpen(false);
          }}
          onClose={() => {
            setConfirmUnpublishPortalModalOpen(false);
          }}
        />
      ) : null}
      <Box
        className={`portalmanager 
          ${hasUnpublishedChanges ? 'portalmanager--subheader___' : ''}`}
      >
        <Box className="portalmanager__header" shadow="xs">
          <Flex
            justify="start"
            align="center"
            direction="row"
            gap={30}
            style={{
              height: 'inherit',
            }}
          >
            <Flex
              style={{
                width: '400px',
              }}
            >
              <IconButton
                icon={Home}
                kind="tertiary"
                size="medium"
                disabled={view === 'dashboard' ? true : false}
                onClick={() => {
                  setHelpMenuOpen(false);
                  setView('dashboard');
                }}
                className="portalmanager__header__button"
              />

              <Button
                kind="tertiary"
                size="medium"
                disabled={view === 'pages' ? true : false}
                onClick={() => {
                  setHelpMenuOpen(false);
                  setView('pages');
                }}
                className="portalmanager__header__button"
                style={{
                  marginLeft: '9px',
                }}
              >
                Builder
              </Button>
              <Button
                kind="tertiary"
                size="medium"
                disabled={view === 'theme' ? true : false}
                onClick={() => {
                  setHelpMenuOpen(false);
                  setView('theme');
                }}
                className="portalmanager__header__button"
              >
                Theme
              </Button>
              <Button
                kind="tertiary"
                size="medium"
                disabled={view === 'settings' ? true : false}
                onClick={() => {
                  setHelpMenuOpen(false);
                  setView('settings');
                }}
                className="portalmanager__header__button"
              >
                Settings
              </Button>

              {/* HELP */}
              {screenMode !== 'small' ? (
                <Dialog
                  content={
                    <DialogContentContainer
                      size="none"
                      style={{
                        overflow: 'hidden',
                      }}
                    >
                      <Menu size="medium">
                        <MenuItem
                          title="How to use"
                          icon={Academy}
                          onClick={() => {
                            setHelpMenuOpen(false);
                            monday.execute('openLinkInTab', { url: 'https://getgorilla.app/products/client-portal/docs/introduction' });
                          }}
                          className="customMenuItem"
                        />
                        <MenuItem
                          title="Video tutorials"
                          icon={Video}
                          onClick={() => {
                            setHelpMenuOpen(false);
                            monday.execute('openLinkInTab', {
                              url: 'https://www.youtube.com/playlist?list=PLNt6YgM15GuH5MiCYeyfcrAk4xse57tNs',
                            });
                          }}
                          className="customMenuItem"
                        />
                        <MenuItem
                          title="What's new?"
                          icon={Announcement}
                          onClick={() => {
                            setHelpMenuOpen(false);
                            monday.execute('openLinkInTab', { url: 'https://getgorilla.app/products/client-portal/docs/release-notes' });
                          }}
                          className="customMenuItem"
                        />
                        <MenuDivider className="customMenuDivider" />
                        {/* 
                        <MenuItem
                          title="Product roadmap"
                          icon={Completed}
                          onClick={() => {
                            setHelpMenuOpen(false);
                            monday.execute('openLinkInTab', { url: 'https://clientportalbuilder.com/roadmap' });
                          }}
                          className="customMenuItem"
                        />
                        */}
                        <MenuItem
                          title="Product website"
                          icon={ExternalPage}
                          onClick={() => {
                            setHelpMenuOpen(false);
                            monday.execute('openLinkInTab', { url: 'https://getgorilla.app/products/client-portal' });
                          }}
                          className="customMenuItem"
                        />
                        <MenuDivider className="customMenuDivider" />
                        <MenuItem
                          title="Book a demo"
                          icon={Calendar}
                          onClick={() => {
                            setHelpMenuOpen(false);
                            monday.execute('openLinkInTab', { url: 'https://cal.com/gorilla-apps/client-portal-builder-demo' });
                          }}
                          className="customMenuItem"
                        />
                        <MenuItem
                          title="Contact us"
                          icon={Email}
                          onClick={() => {
                            setHelpMenuOpen(false);
                            monday.execute('openLinkInTab', { url: 'https://getgorilla.app/contact' });
                          }}
                          className="customMenuItem"
                        />
                      </Menu>
                    </DialogContentContainer>
                  }
                  middleware={[shift({ mainAxis: false })]}
                  showTrigger={[]}
                  open={helpMenuOpen}
                  instantShowAndHide
                  zIndex={3000}
                  moveBy={{ main: 0, secondary: 60 }}
                  position="bottom"
                  onClickOutside={() => {
                    setHelpMenuOpen(false);
                  }}
                >
                  <Button
                    kind="tertiary"
                    size="medium"
                    rightIcon={helpMenuOpen ? DropdownChevronUp : DropdownChevronDown}
                    onClick={() => {
                      setHelpMenuOpen(!helpMenuOpen);
                    }}
                  >
                    Help
                  </Button>
                </Dialog>
              ) : null}
            </Flex>

            <Flex
              justify="center"
              gap={6}
              style={{
                flex: 1,
              }}
            >
              <Dialog
                content={
                  <DialogContentContainer
                    size="none"
                    style={{
                      overflow: 'hidden',
                    }}
                  >
                    <Box>
                      <Flex direction="column" justify="start" align="start" style={{ width: '100%' }}>
                        <Box
                          margin="medium"
                          marginBottom="small"
                          style={{
                            width: 'calc(100% - 32px)',
                          }}
                        >
                          <Flex direction="row" justify="start" align="start" gap={16} style={{ width: '100%' }}>
                            <Flex gap={6} style={{ flex: 1 }}>
                              <Icon icon={Clipboard} size={16} />
                              <Text type="text3" weight="bold" color="primary" align="start">
                                Unpublished changes
                              </Text>
                            </Flex>
                            <Text type="text3" weight="normal" color="secondary" align="start">
                              {hasUnpublishedChanges ? 'Yes' : 'No'}
                            </Text>
                          </Flex>
                        </Box>
                        <Divider withoutMargin />
                        <Box
                          margin="medium"
                          marginTop="small"
                          marginBottom={portal?.lastPublishedAt ? 'small' : 'medium'}
                          style={{
                            width: 'calc(100% - 32px)',
                          }}
                        >
                          <Flex direction="row" justify="start" align="start" gap={16} style={{ width: '100%' }}>
                            <Flex gap={6} style={{ flex: 1 }}>
                              <Icon icon={TurnInto} size={16} />
                              <Text type="text3" weight="bold" color="primary" align="start">
                                Client Portal is online
                              </Text>
                            </Flex>
                            <Text type="text3" weight="normal" color="secondary" align="start">
                              {portal?.lastPublishedAt ? 'Yes' : 'No'}
                            </Text>
                          </Flex>
                        </Box>
                        {portal?.lastPublishedAt ? (
                          <>
                            <Divider withoutMargin />
                            <Box
                              margin="medium"
                              marginTop="small"
                              style={{
                                width: 'calc(100% - 32px)',
                              }}
                            >
                              <Flex direction="row" justify="start" align="start" gap={16} style={{ width: '100%' }}>
                                <Flex gap={6} style={{ flex: 1 }}>
                                  <Icon icon={Time} size={16} />
                                  <Text type="text3" weight="bold" color="primary" align="start">
                                    Last published
                                  </Text>
                                </Flex>
                                <Text type="text3" weight="normal" color="secondary" align="start">
                                  {last(portal?.lastPublishedAt ? [getRelativeTimeString(portal?.lastPublishedAt)] : ['–'])}
                                </Text>
                              </Flex>
                            </Box>
                          </>
                        ) : null}
                      </Flex>
                    </Box>
                  </DialogContentContainer>
                }
                middleware={[shift({ mainAxis: false })]}
                //open
                showTrigger={['mouseenter']}
                hideTrigger={['mouseleave']}
                instantShowAndHide
                tooltip
                zIndex={3000}
                moveBy={{ main: 10, secondary: 0 }}
                position="bottom"
              >
                <Flex gap={10} style={{ cursor: 'default' }}>
                  {isSavingSettings ? <Loader size="xs" /> : null}
                  <Heading
                    type="h3"
                    weight="bold"
                    align="center"
                    withoutTooltip
                    style={{
                      maxWidth: screenMode === 'small' ? 'calc(100vw - 690px)' : 'calc(100vw - 840px)',
                    }}
                  >
                    {portalSettings.name}
                  </Heading>
                  {hasUnpublishedChanges ? <Label text="Draft" color="egg_yolk" kind="fill" className="custom__label--draft" /> : null}
                </Flex>
              </Dialog>
            </Flex>

            <Box
              style={{
                width: '400px',
              }}
            >
              <Flex justify="end" gap={16}>
                {view === 'dashboard' ? (
                  <Button
                    leftIcon={Settings}
                    kind="primary"
                    size="medium"
                    onClick={() => {
                      setView('pages');
                    }}
                  >
                    Start building
                  </Button>
                ) : (
                  <>
                    {!portal?.lastPublishedAt ? (
                      <SplitButton
                        leftIcon={screenMode !== 'small' ? ExternalPage : undefined}
                        children={'Preview draft'}
                        onClick={() => {
                          // if no draft has been saved yet, save it first
                          if (!portal.lastUpdatedAt) {
                            triggerSaveSettings(portalSettings).then(() => {
                              openPreviewWindow(clientBoard!.id, effectivePreviewClient!, 'draft');
                            });
                          } else {
                            openPreviewWindow(clientBoard!.id, effectivePreviewClient!, 'draft');
                          }
                        }}
                        dialogPaddingSize="none"
                        secondaryDialogPosition="bottom-end"
                        kind="secondary"
                        size="medium"
                        color="primary"
                        disabled={
                          !portalSettings.pages.length ||
                          isUnpublishing ||
                          !Array.isArray(clientBoardItems) ||
                          !clientBoardItems.length ||
                          !clientBoard
                        }
                        shouldCloseOnClickInsideDialog
                        className="custom__splitbutton"
                        secondaryDialogContent={
                          <SplitButtonMenu className="customSplitMenu customSplitMenuAuto">
                            <MenuItem
                              icon={Person}
                              title="Change preview client"
                              className="customMenuItem"
                              onClick={() => setChangePreviewClientModalOpen(true)}
                            />
                          </SplitButtonMenu>
                        }
                      />
                    ) : (
                      <SplitButton
                        leftIcon={screenMode !== 'small' ? ExternalPage : undefined}
                        children={'Preview draft'}
                        onClick={() => {
                          // if no draft has been saved yet, save it first
                          if (!portal.lastUpdatedAt) {
                            triggerSaveSettings(portalSettings).then(() => {
                              openPreviewWindow(clientBoard!.id, effectivePreviewClient!, 'draft');
                            });
                          } else {
                            openPreviewWindow(clientBoard!.id, effectivePreviewClient!, 'draft');
                          }
                        }}
                        dialogPaddingSize="none"
                        secondaryDialogPosition="bottom-end"
                        kind="secondary"
                        size="medium"
                        color="primary"
                        disabled={
                          !portalSettings.pages.length ||
                          isUnpublishing ||
                          !Array.isArray(clientBoardItems) ||
                          !clientBoardItems.length ||
                          !clientBoard
                        }
                        shouldCloseOnClickInsideDialog
                        className="custom__splitbutton"
                        secondaryDialogContent={
                          <SplitButtonMenu className="customSplitMenu customSplitMenuAuto">
                            <MenuItem
                              icon={ExternalPage}
                              title="Preview published portal"
                              className="customMenuItem"
                              // @ts-ignore
                              disabled={!portal?.lastPublishedAt}
                              onClick={() => {
                                if (clientBoard && effectivePreviewClient && portal?.lastPublishedAt) {
                                  openPreviewWindow(clientBoard.id, effectivePreviewClient, 'public');
                                }
                              }}
                            />
                            <MenuDivider className="customMenuDivider" />
                            <MenuItem
                              icon={Person}
                              title="Change preview client"
                              className="customMenuItem"
                              onClick={() => setChangePreviewClientModalOpen(true)}
                            />
                          </SplitButtonMenu>
                        }
                      />
                    )}

                    <SplitButton
                      leftIcon={screenMode !== 'small' ? TurnInto : undefined}
                      children={isUnpublishing ? 'Unpublishing' : 'Publish'}
                      onClick={() => {
                        setPublishModal(true);
                      }}
                      dialogPaddingSize="none"
                      kind={isUnpublishing || !portalSettings.pages.length ? 'secondary' : 'primary'}
                      color="primary"
                      disabled={!portalSettings.pages.length || isUnpublishing}
                      shouldCloseOnClickInsideDialog
                      secondaryDialogContent={
                        <SplitButtonMenu className="customSplitMenu">
                          <MenuItem
                            icon={Globe}
                            title="Open portal"
                            className="customMenuItem"
                            // @ts-ignore
                            disabled={!portal?.lastPublishedAt || isUnpublishing}
                            onClick={() => {
                              {
                                if (portal?.lastPublishedAt) {
                                  monday.execute('openLinkInTab', { url: getClientPortalUrl(portal?.id, registryEntries) });
                                }
                              }
                            }}
                          />
                          <MenuDivider className="customMenuDivider" />
                          <MenuItem
                            icon={Retry}
                            title="Discard changes"
                            className={
                              !portal?.lastPublishedAt || isUnpublishing || !hasUnpublishedChanges
                                ? 'customMenuItem'
                                : 'customMenuItem customMenuItem--Red__'
                            }
                            // @ts-ignore
                            disabled={!portal?.lastPublishedAt || isUnpublishing || !hasUnpublishedChanges}
                            onClick={() => {
                              {
                                portal?.lastPublishedAt && setConfirmDiscardDraftModalOpen(true);
                              }
                            }}
                          />
                          <MenuItem
                            icon={Deactivate}
                            title="Unpublish portal"
                            className={
                              !portal?.lastPublishedAt || isUnpublishing ? 'customMenuItem' : 'customMenuItem customMenuItem--Red__'
                            }
                            // @ts-ignore
                            disabled={!portal?.lastPublishedAt || isUnpublishing}
                            onClick={() => {
                              {
                                portal?.lastPublishedAt && setConfirmUnpublishPortalModalOpen(true);
                              }
                            }}
                          />
                        </SplitButtonMenu>
                      }
                    />
                  </>
                )}
              </Flex>
            </Box>
          </Flex>
        </Box>
        <Box
          className={`portalmanager__content 
            ${hasUnpublishedChanges && view !== 'dashboard' ? 'portalmanager__content--subheader___' : ''}`}
          backgroundColor="greyBackgroundColor"
        >
          {content}
        </Box>
      </Box>
    </div>
  );
}

export default PortalManager;
